import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { Input } from '@/components/ui/Input';
import { t } from '@/lib/i18n';
import type { EditorState } from '../roleContractEditorState';
import { COMPONENT_KEYS, COMPONENT_LABELS } from '../roleContractEditorState';

interface Props {
  state: EditorState;
  setState: React.Dispatch<React.SetStateAction<EditorState>>;
}

export function UpgradeRulesSection({ state, setState }: Props) {
  // Sven dogfood 2026-05-03: pick options used to render raw enum keys
  // (purpose_scope, etc.). Use the human labels.
  const componentOptions = COMPONENT_KEYS.map((k) => ({
    value: k,
    label: COMPONENT_LABELS[k] ?? k,
  }));
  return (
    <Card>
      <CardHeader><CardTitle>{t('Upgrade Rules')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {/* Sven dogfood 2026-05-03: explain the whole feature so
            founders don't have to reverse-engineer it from labels. */}
        <p className="text-sm text-muted-foreground">
          {t('How this Role Contract evolves over time. As ROROS observes the role\'s performance (FTT rate, decision latency, deliverable rejections), it can propose refinements to specific sections. You decide which sections may change automatically and which always need your sign-off.')}
        </p>
        <div>
          <MultiSelect
            label={t('Auto-suggest sections')}
            options={componentOptions}
            value={state.upgradeAutoSuggest}
            onChange={(v) => setState((s) => ({ ...s, upgradeAutoSuggest: v }))}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Sections in this list may be updated by the system within the monthly cap. Good candidates: QC Duties, Tools & Visibility.')}
          </p>
        </div>
        <div>
          <MultiSelect
            label={t('Requires CEO review')}
            options={componentOptions}
            value={state.upgradeRequiresCeo}
            onChange={(v) => setState((s) => ({ ...s, upgradeRequiresCeo: v }))}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Sections in this list always need explicit sign-off from a CEO-equivalent before any change applies. Good candidates: Decisions, Outcomes, Reports & Collaboration.')}
          </p>
        </div>
        <div>
          <Input
            type="number"
            min="0"
            step="1"
            label={t('Max auto-upgrades per month')}
            value={state.upgradeMaxPerMonth}
            onChange={(e) => setState((s) => ({ ...s, upgradeMaxPerMonth: e.target.value }))}
            placeholder={t('e.g. 3')}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Rate limit. 0 = no auto-upgrades; the system can still propose changes for manual review.')}
          </p>
        </div>
        <p className="text-xs text-warning-700 bg-warning-50 border border-warning-200 rounded p-2">
          {t('Note: the upgrade scheduler is not yet active. Settings persist; auto-upgrades will start running in a future milestone.')}
        </p>
      </CardContent>
    </Card>
  );
}
